import React, { FC, useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { globalStore } from '../utils/globalStore';
import { getMenuPermissionsApi } from '../service/menu';
import { getUserInfoApi } from '../service/user';
import { Menu } from '../service/menu/types';
import Auth from "./auth";

// 静态导入常用组件（不需要动态导入）
import { Index403 } from "../pages/403/Index403";
import { PageLoading } from "../components/common/Loading";
import { Login } from "../pages/login/Login";
import { Register } from "../pages/register/Register";
import { Index404 } from "../pages/404/Index404";
import { Home } from "../pages/home/Home";
import { VideoList } from "../pages/videoList/VideoList";
import { Profile } from "../pages/profile/Profile";
import { UploadVideo } from "../pages/uploadVideo/Index";
import { VoiceClone } from "../pages/voiceClone/Index";
import VideoEdit from "../pages/videoEdit";
import { VideoModalTest } from "../pages/test/VideoModalTest";
import { VideoPreview } from "../pages/videoPreview/VideoPreview";
import { VoiceLibrary } from "../pages/voiceLibrary/Index";
import { SeriesListIn } from "../pages/seriesListIn/SeriesListIn";

// 动态组件映射表 - 使用 React.lazy 解决生产环境路径问题
const dynamicComponentMap: Record<string, React.LazyExoticComponent<React.ComponentType<any>>> = {
  'pages/profile/Profile': React.lazy(() => import('../pages/profile/Profile').then(m => ({ default: m.Profile }))),
  'pages/manage/BasicInfo': React.lazy(() => import('../pages/manage/BasicInfo').then(m => ({ default: m.BasicInfo }))),
  'pages/manage/GroupManage': React.lazy(() => import('../pages/manage/GroupManage').then(m => ({ default: m.GroupManage }))),
  'pages/manage/JujiManage': React.lazy(() => import('../pages/manage/JujiManage').then(m => ({ default: m.JujiManage }))),
  'pages/manage/system/MenuManage': React.lazy(() => import('../pages/manage/system/MenuManage').then(m => ({ default: m.MenuManage }))),
  'pages/manage/system/RoleManage': React.lazy(() => import('../pages/manage/system/RoleManage').then(m => ({ default: m.RoleManage }))),
  'pages/manage/system/UserInfo': React.lazy(() => import('../pages/manage/system/UserInfo').then(m => ({ default: m.UserInfo }))),
  'pages/manage/system/log/Loginlog': React.lazy(() => import('../pages/manage/system/log/Loginlog').then(m => ({ default: m.Loginlog }))),
  'pages/manage/system/log/Operationlog': React.lazy(() => import('../pages/manage/system/log/Operationlog').then(m => ({ default: m.Operationlog }))),
  // 'pages/videoDetail/Detail': React.lazy(() => import('../pages/videoDetail/Detail').then(m => ({ default: m.Detail }))),
  // 'pages/listenSound/Index': React.lazy(() => import('../pages/listenSound/Index').then(m => ({ default: m.ListenSound }))),
  // 'pages/subtitleDemo': React.lazy(() => import('../pages/subtitleDemo')),
  // 'pages/skeletonDemo/SkeletonDemo': React.lazy(() => import('../pages/skeletonDemo/SkeletonDemo')),
};

// 路由配置接口
interface RouteConfig {
  path: string;
  component: string;
  requireAuth: boolean;
  permission?: string;
  parentId?: number;
  orderNum?: number;
  icon?: string;
  menuName?: string;
}

// 静态路由配置（不需要权限控制）
const staticRouteConfigs: RouteConfig[] = [
  { path: '/login', component: 'pages/login/Login', requireAuth: false },
  { path: '/register', component: 'pages/register/Register', requireAuth: false },
  { path: '/404', component: 'pages/404/Index404', requireAuth: false },
  { path: '/video-preview', component: 'pages/videoPreview/VideoPreview', requireAuth: false },
  { path: '/test-video-modal', component: 'pages/test/VideoModalTest', requireAuth: false },
];

// 默认路由配置（需要权限控制（判断是否有token））
const defaultRouteConfigs: RouteConfig[] = [
  { path: '/home', component: 'pages/home/Home', requireAuth: true },
  { path: '/videoList', component: 'pages/videoList/VideoList', requireAuth: true },
  { path: '/video-detail', component: 'pages/videoDetail/Detail', requireAuth: true },
  { path: '/videoEdit/:id', component: 'pages/videoEdit', requireAuth: true },
  { path: '/uploadVideo', component: 'pages/uploadVideo/Index', requireAuth: true },
  { path: '/voiceClone', component: 'pages/voiceClone/Index', requireAuth: true },
  { path: '/listenSound', component: 'pages/listenSound/Index', requireAuth: true },
  { path: '/subtitle-demo/:id?', component: 'pages/subtitleDemo', requireAuth: true },
  { path: '/songs', component: 'pages/voiceLibrary/Index', requireAuth: true },
  { path: '/seriesListIn', component: 'pages/seriesListIn/SeriesListIn', requireAuth: true },  
  // 独立的管理页面路由
//   { path: '/profile', component: 'pages/profile/Profile', requireAuth: true },
//   { path: '/manage/menu', component: 'pages/manage/MenuManagement', requireAuth: true, permission: 'admin:all' },
//   { path: '/manage/juji', component: 'pages/manage/JujiManage', requireAuth: true },
//   { path: '/manage/group', component: 'pages/manage/GroupManage', requireAuth: true },
//   { path: '/manage/basic', component: 'pages/manage/BasicInfo', requireAuth: true },
  
];

// 简化的路由工具函数
const mergeRoutes = (staticRoutes: RouteConfig[], dynamicRoutes: RouteConfig[], defaultRoutes: RouteConfig[]): RouteConfig[] => {
  const pathMap = new Map<string, RouteConfig>();
  
  // 添加静态路由（优先级最高）
  staticRoutes.forEach(route => pathMap.set(route.path, route));
  
  // 添加动态路由（优先级中等）
  dynamicRoutes.forEach(route => {
    if (!pathMap.has(route.path)) {
      pathMap.set(route.path, route);
    }
  });
  
  // 添加默认路由（优先级最低）
  defaultRoutes.forEach(route => {
    if (!pathMap.has(route.path)) {
      pathMap.set(route.path, route);
    }
  });
  
  return Array.from(pathMap.values());
};

// 递归处理菜单数据，提取所有路由
const extractRoutesFromMenu = (menu: Menu.Menu): RouteConfig[] => {
  const routes: RouteConfig[] = [];
  
  
  // 处理当前菜单项
  if (!menu.hidden && menu.path && menu.component) {
    // 只有明确设置了权限的路由才需要权限检查
    const menuPerms = (menu as any).perms;
    const hasExplicitPermission = menuPerms && menuPerms.trim() !== '';
    
    // 处理component值：去掉前面的"/"
    const component = menu.component.replace(/^\/+/, '');
    
    const route = {
      path: menu.path,
      component: component,
      requireAuth: true,
      permission: hasExplicitPermission ? menuPerms : undefined, // 只有明确有权限时才设置
      icon: menu.meta?.icon,
      menuName: menu.meta?.title
    };
    routes.push(route);
  } else {
  }
  
  // 递归处理子菜单
  if (menu.children && menu.children.length > 0) {
    menu.children.forEach(child => {
      routes.push(...extractRoutesFromMenu(child));
    });
  }
  
  return routes;
};

const menuToRoutes = (menuList: Menu.Menu[]): RouteConfig[] => {
  const allRoutes: RouteConfig[] = [];
  
  menuList.forEach(menu => {
    const routes = extractRoutesFromMenu(menu);
    allRoutes.push(...routes);
  });
  
  // 所有动态路由已生成
  return allRoutes;
};
// 约定式路由：菜单中的component字段直接对应文件路径
// 例如：component: 'pages/manage/BasicInfo' 对应文件 src/pages/manage/BasicInfo.tsx

// 将新菜单格式转换为现有代码期望的格式
const convertNewMenuToOldFormat = (newMenu: Menu.NewMenu): Menu.Menu => {
  // 处理component值：去掉前面的"/"
  const component = newMenu.component ? newMenu.component.replace(/^\/+/, '') : '';
  
  // 处理路径：如果path为空或null，使用空字符串
  const path = newMenu.path || '';
  
  // 处理图标：如果图标包含 "fa:" 前缀，转换为 FontAwesome 格式
  let icon = newMenu.icon || '';
  if (icon && icon.startsWith('fa:')) {
    icon = icon.replace('fa:', 'fa-');
  }
  
  return {
    name: newMenu.name,
    path: path,
    hidden: !newMenu.visible, // visible: false -> hidden: true
    component: component,
    sort: newMenu.sort, // 保留排序字段
    meta: {
      title: newMenu.name,
      icon: icon,
      noCache: !newMenu.keepAlive, // keepAlive: false -> noCache: true
      link: null
    },
    children: newMenu.children && newMenu.children.length > 0
      ? newMenu.children.map(child => convertNewMenuToOldFormat(child)).sort((a, b) => (a.sort || 0) - (b.sort || 0)) // 对子菜单按 sort 排序
      : []
  };
};

// 递归检查菜单权限
const checkMenuPermission = (menu: any, permission: string): boolean => {
  // 检查当前菜单的权限 - 只检查 meta.perms 字段
  if (menu.meta?.perms === permission) {
    return true;
  }
  
  // 递归检查子菜单
  if (menu.children && menu.children.length > 0) {
    return menu.children.some((child: any) => checkMenuPermission(child, permission));
  }
  
  return false;
};

// 权限检查函数 - 适配新的菜单数据结构
const hasPermission = (permission: string): boolean => {
  // 1. 先查内存 (Store)
  let permissions = globalStore.getPermissions();

  // === 🚑 修复补丁开始：内存丢失时的自动急救 ===
  if (!permissions || permissions.length === 0) {
    try {
      // 从 localStorage 恢复权限数据
      const cachedUserStr = localStorage.getItem('globalUserInfo');
      if (cachedUserStr) {
        const cachedUser = JSON.parse(cachedUserStr);
        // 如果缓存里有 permissions，临时用它来救急
        if (cachedUser.permissions && cachedUser.permissions.length > 0) {
          console.warn(`⚠️ [权限恢复] 检测到内存权限丢失，已使用本地缓存兜底: ${permission}`);
          permissions = cachedUser.permissions;
          // 可选：顺便把数据塞回 store，防止下次还读缓存
          // 注意：这里不自动恢复，避免覆盖可能正在更新的数据
        }
      }
    } catch (e) {
      console.error('尝试恢复权限失败', e);
    }
  }
  // === 🚑 修复补丁结束 ===

  // 2. 检查权限数组
  if (permissions && permissions.length > 0 && permissions.includes(permission)) {
    return true;
  }

  // 3. 检查菜单 meta 权限 (保持原有逻辑)
  const menuList = globalStore.getMenuList();
  
  return menuList.some(menu => checkMenuPermission(menu, permission));
};

// 权限守卫组件 - 解耦权限检查逻辑
const PermissionGuard: React.FC<{ permission?: string; children: React.ReactNode }> = ({ 
  permission, 
  children 
}) => {
  // 1. 无权限要求的直接放行
  if (!permission) {
    return <>{children}</>;
  }
  
  // 2. 尝试获取菜单数据
  let menuList = globalStore.getMenuList();

  // === 🚑 修复补丁：如果菜单也丢了，尝试从 Storage 恢复 ===
  if (!menuList || menuList.length === 0) {
    const cachedMenuStr = localStorage.getItem('globalMenuList');
    if (cachedMenuStr) {
      try {
        menuList = JSON.parse(cachedMenuStr);
        // 这里的 consoleLog 仅调试用
        // console.log('从缓存恢复了菜单列表用于鉴权');
      } catch(e) {
        console.error('从缓存恢复菜单列表失败', e);
      }
    }
  }
  // === 结束补丁 ===

  // 3. 如果实在拿不到菜单（说明是第一次加载且接口还没回），暂时放行防止白屏
  // 注意：这里保留你的逻辑，防止刚刷新页面时全部 403
  if (!menuList || menuList.length === 0) {
    return <>{children}</>;
  }
  
  // 4. 核心鉴权
  // 因为 hasPermission 我们上面已经加强了，这里直接调用即可
  if (!hasPermission(permission)) {
    // 调试模式下可以打印一下为什么挂了
    // console.log(`鉴权失败: ${permission}, 当前权限池:`, globalStore.getPermissions());
    return <Index403 />;
  }
  
  return <>{children}</>;
};

// 错误组件 - 统一的错误提示
const ErrorComponent: React.FC<{ componentName: string }> = ({ componentName }) => (
  <div style={{ 
    padding: '20px', 
    textAlign: 'center', 
    color: '#666',
    background: '#f5f5f5',
    borderRadius: '8px',
    margin: '20px'
  }}>
    <h3>页面加载失败</h3>
    <p>组件 {componentName} 加载失败，请刷新页面重试</p>
    <button 
      onClick={() => window.location.reload()} 
      style={{
        padding: '8px 16px',
        background: '#1890ff',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer'
      }}
    >
      刷新页面
    </button>
  </div>
);



const getDynamicComponent = (componentName: string) => {
  // 优先使用组件映射表中的 lazy 组件
  const LazyComponent = dynamicComponentMap[componentName];
  
  if (LazyComponent) {
    return (
      <React.Suspense fallback={<PageLoading />}>
        <LazyComponent />
      </React.Suspense>
    );
  }
  
  // 如果映射表中没有，直接返回错误组件
  return <ErrorComponent componentName={componentName} />;
};

// 动态路由组件
export const DynamicRoutes: FC = () => {
  const [routes, setRoutes] = useState<RouteConfig[]>(() => {
    // 立即使用默认路由，避免白屏
    return mergeRoutes(
      staticRouteConfigs,
      [],
      defaultRouteConfigs
    );
  });
  const [menuList, setMenuList] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadRoutes = async () => {
      try {
        // 检查是否有token，如果没有token就不加载菜单数据
        const token = localStorage.getItem("token");
        if (!token) {
          console.log('没有token，跳过菜单数据加载');
          // 使用默认路由
          const fallbackRoutes = mergeRoutes(
            staticRouteConfigs,
            [],
            defaultRouteConfigs
          );
          setRoutes(fallbackRoutes);
          setIsLoading(false);
          return;
        }

        // 获取菜单权限和用户信息（新接口已包含用户信息，不再需要单独请求）
        const menuResponse = await getMenuPermissionsApi();
        
        // 处理新的响应格式
        let menuList: Menu.Menu[] = [];
        let userInfo: any = null;
        let roles: string[] = [];
        let permissions: string[] = [];
        
        if (menuResponse && menuResponse.code === 0 && menuResponse.data) {
          const responseData = menuResponse.data;
          
          // 转换菜单数据格式
          if (responseData.menus && Array.isArray(responseData.menus)) {
            menuList = responseData.menus.map(menu => convertNewMenuToOldFormat(menu));
          }
          
          // 保存用户信息
          if (responseData.user) {
            userInfo = {
              ...responseData.user,
              roles: responseData.roles || [],
              permissions: responseData.permissions || []
            };
          }
          
          roles = responseData.roles || [];
          permissions = responseData.permissions || [];
        } else {
          console.error('获取菜单权限失败:', menuResponse);
        }
        
        // 保存用户信息到全局状态
        if (userInfo) {
          globalStore.setUserInfo(userInfo);
        }
        
        // 更新全局状态和本地状态
        globalStore.setMenuList(menuList);
        globalStore.setInitialized(true);
        setMenuList(menuList);
        
        // 基于菜单权限生成动态路由
        const allRoutes = generateDynamicRoutes(menuList);
        
        setRoutes(allRoutes);
      } catch (error) {
        console.error('加载菜单权限失败:', error);
        // 如果加载失败，使用默认路由
        const fallbackRoutes = mergeRoutes(
          staticRouteConfigs,
          [],
          defaultRouteConfigs
        );
        setRoutes(fallbackRoutes);
      } finally {
        setIsLoading(false);
      }
    };

    loadRoutes();
  }, []);

  // 基于菜单权限生成动态路由
  const generateDynamicRoutes = (menuList: any[]): RouteConfig[] => {
    // 生成动态路由
    const dynamicRoutes = menuToRoutes(menuList);
    
    // 合并所有路由配置
    const allRoutes = mergeRoutes(
      staticRouteConfigs,
      dynamicRoutes,
      defaultRouteConfigs
    );
    
    return allRoutes;
  };

  // 渲染路由组件
  const renderRoute = (route: RouteConfig, menuList: any[] = []) => {
    const { path, component, requireAuth, permission } = route;
    
    // 静态组件映射（不需要动态导入的组件）
    const staticComponents: Record<string, React.ComponentType> = {
      'pages/login/Login': Login,
      'pages/register/Register': Register,
      'pages/404/Index404': Index404,
      'pages/home/Home': Home,
      'pages/videoList/VideoList': VideoList,
      'pages/profile/Profile': Profile,
      'pages/uploadVideo/Index': UploadVideo,
      'pages/voiceClone/Index': VoiceClone,
      'pages/videoEdit': VideoEdit,
      'pages/test/VideoModalTest': VideoModalTest,
      'pages/videoPreview/VideoPreview': VideoPreview,
      'pages/voiceLibrary/Index': VoiceLibrary,
      'pages/seriesListIn/SeriesListIn': SeriesListIn,
    };
    
    // 检查是否为静态组件
    const StaticComponent = staticComponents[component];
        
    // 创建带权限检查的组件包装器 - 使用解耦的权限守卫
    const ProtectedComponent = () => {
      return (
        <PermissionGuard permission={permission}>
          {/* 如果是静态组件，直接渲染 */}
          {StaticComponent ? (
            <StaticComponent />
          ) : (
            getDynamicComponent(component)
          )}
        </PermissionGuard>
      );
    };
    
    // 特殊处理Profile页面的嵌套路由
    if (path === '/profile') {
      // 查找Profile菜单及其子菜单（个人中心的path可能是null，通过component或name匹配）
      const profileMenu = menuList.find(menu => 
        menu.path === '/profile' || 
        menu.component === 'pages/profile/Profile' ||
        menu.name === '个人中心'
      );
      const profileMenus = profileMenu?.children || [];
      // Profile菜单数据已生成
      
      // 递归函数：提取相对路径（相对于 /profile）
      const extractRelativePath = (fullPath: string): string => {
        if (!fullPath || fullPath === '/profile') return '';
        
        // 严格提取 /profile/ 后的部分（适配所有/profile开头的子路由）
        const profilePrefix = '/profile/';
        if (fullPath.startsWith(profilePrefix)) {
          const relativePath = fullPath.slice(profilePrefix.length);
          return relativePath;
        }
        
        return fullPath;
      };
      
      // 递归函数：生成所有层级的嵌套路由
      // parentFullPath: 父菜单的完整路径（如 '/profile'），用于计算子路由的相对路径
      // isNested: 是否在嵌套 Route 中，如果是，子路由路径应该相对于父路径
      const renderNestedRoutes = (menus: any[], parentFullPath: string = '/profile', isNested: boolean = false): React.ReactNode[] => {
        const routes: React.ReactNode[] = [];
        
        menus.forEach((menu: any) => {
          // 跳过隐藏的菜单，但不跳过空路径/空组件的父菜单（如系统管理）
          if (menu.hidden) {
            return;
          }
          
          const hasChildren = menu.children && menu.children.length > 0;
          const hasComponent = !!menu.component;
          const hasPath = !!menu.path && menu.path !== '/profile';
          
          // 情况1：有路径+有组件 → 生成子路由（如角色管理、用户管理、权限管理）
          if (hasPath && hasComponent) {
            // 提取相对路径（/profile/role → role）
            const relativePath = extractRelativePath(menu.path);
            if (!relativePath) {
              return;
            }
            
            const component = menu.component.replace(/^\/+/, '');
            routes.push(
              <Route 
                key={menu.id || menu.name || menu.path} 
                path={relativePath} 
                element={getDynamicComponent(component)} 
              />
            );
            
            // 如果有子菜单，递归处理
            if (hasChildren) {
              const childRoutes = renderNestedRoutes(menu.children, parentFullPath, isNested);
              routes.push(...childRoutes);
            }
          }
          // 情况2：无路径/无组件，但有子菜单 → 递归处理子菜单（如系统管理、日志管理）
          else if (hasChildren) {
            // 递归处理子菜单，父路径沿用当前parentFullPath（因为当前菜单无路径）
            const childRoutes = renderNestedRoutes(menu.children, parentFullPath, isNested);
            routes.push(...childRoutes);
          }
          // 情况3：有组件但无路径（如基础信息，path为null但有component）
          else if (hasComponent && !hasPath) {
            // 这种情况可能需要特殊处理，暂时跳过
            console.log('跳过无路径但有组件的菜单:', menu.name);
          }
        });
        
        return routes;
      };
      
      // 查找第一个有路径的子菜单作为默认路由
      const findFirstValidPath = (menus: any[]): string | null => {
        for (const menu of menus) {
          if (menu.hidden) continue;
          
          // 如果菜单有路径和组件，直接返回
          if (menu.path && menu.component && menu.path.startsWith('/profile/')) {
            const relativePath = extractRelativePath(menu.path);
            if (relativePath) {
              return relativePath;
            }
          }
          
          // 如果有子菜单，递归查找
          if (menu.children && menu.children.length > 0) {
            const childPath = findFirstValidPath(menu.children);
            if (childPath) {
              return childPath;
            }
          }
        }
        return null;
      };
      
      // 收集所有实际会生成的路由路径
      const collectedPaths: string[] = [];
      
      // 修改 renderNestedRoutes，在生成路由的同时收集路径
      const renderNestedRoutesWithPathCollection = (menus: any[], parentFullPath: string = '/profile', isNested: boolean = false): React.ReactNode[] => {
        const routes: React.ReactNode[] = [];
        
        menus.forEach((menu: any) => {
          if (menu.hidden) {
            return;
          }
          
          const hasChildren = menu.children && menu.children.length > 0;
          const hasComponent = !!menu.component;
          const hasPath = !!menu.path && menu.path !== '/profile';
          
          if (hasPath && hasComponent) {
            const relativePath = extractRelativePath(menu.path);
            if (relativePath) {
              // 收集路径
              collectedPaths.push(relativePath);
              
              const component = menu.component.replace(/^\/+/, '');
              routes.push(
                <Route 
                  key={menu.id || menu.name || menu.path} 
                  path={relativePath} 
                  element={getDynamicComponent(component)} 
                />
              );
              
              if (hasChildren) {
                const childRoutes = renderNestedRoutesWithPathCollection(menu.children, parentFullPath, isNested);
                routes.push(...childRoutes);
              }
            }
          } else if (hasChildren) {
            const childRoutes = renderNestedRoutesWithPathCollection(menu.children, parentFullPath, isNested);
            routes.push(...childRoutes);
          } else if (hasComponent && !hasPath) {
            console.log('跳过无路径但有组件的菜单:', menu.name);
          }
        });
        
        return routes;
      };
      
      const generatedRoutes = renderNestedRoutesWithPathCollection(profileMenus);
      
      // 优先使用实际生成的路由路径（第一个），确保跳转的路径对应的路由确实存在
      const firstValidPath = collectedPaths.length > 0 ? collectedPaths[0] : findFirstValidPath(profileMenus);
      
      // 只有当找到了有效路径且生成了路由时，才进行跳转
      const canNavigate = firstValidPath && generatedRoutes.length > 0;
      
      return (
        <Route 
          key={path} 
          path={path} 
          element={<Auth><ProtectedComponent /></Auth>}
        >
          {/* 递归生成所有层级的嵌套路由 */}
          {generatedRoutes}
          {/* Profile 页面的默认子路由 */}
          <Route 
            index 
            element={
              // 如果找到了有效路径且生成了路由，就重定向
              canNavigate ? (
                <Navigate to={firstValidPath} replace />
              ) : (
                // 如果没找到（说明该角色没菜单），渲染一个优雅的空状态
                <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                  <h3>暂无可用功能</h3>
                  <p>当前账号在该模块下没有可访问的菜单，请联系管理员配置。</p>
                </div>
              )
            } 
          />
          {/* 兜底 404 - 只处理子路由的404，不处理 index 路由 */}
          {generatedRoutes.length > 0 && (
            <Route path="*" element={<Navigate to="/404" replace />} />
          )}
        </Route>
      );
    }
    
    if (requireAuth) {
      return (
        <Route 
          key={path} 
          path={path} 
          element={<Auth><ProtectedComponent /></Auth>} 
        />
      );
    } else {
      return (
        <Route 
          key={path} 
          path={path} 
          element={<ProtectedComponent />} 
        />
      );
    }
  };

  return (
    <>
      {isLoading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          background: '#ffffff',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}>
          <PageLoading />
        </div>
      )}
      <Routes>
        {routes.map(route => renderRoute(route, menuList))}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<Index404 />} />
      </Routes>
    </>
  );
};
